<script setup lang="ts">
    import EventIcon from './EventIcon.vue';
    import { computed, inject, nextTick, onMounted, ref, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import { vTooltip } from '@shared/directives';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';
    import { copyToClipboard } from '@views/Web/copyToClipboard';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { ScopeAccess } from '@shared/directives/Scope.access';
    import { Permissions } from '@shared/Root/Permissions';
    import { canWriteKey, eventKey, saveEventPropertyKey } from '@views/Event/EventEditor/event-editor-keys';

    const props = defineProps<{
        eventId: string;
    }>();

    const PIN_LENGTH = 6;

    const event = inject(eventKey)!;
    const saveEventProperty = inject(saveEventPropertyKey)!;
    const canWrite = inject(canWriteKey)!;

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);

    const canReadContacts = computed(() => scopeAccess.hasScope({ Feature: 'Contacts', Permission: Permissions.Read }));
    const publicHost = computed(() => contextAccess.state.currentAccount!.PublicEventUrl.PublicHost);

    const tempPinCode = ref('');

    watch(
        () => event.value?.PublicPin,
        (pin) => (tempPinCode.value = pin ?? ''),
        { immediate: true }
    );

    const pinChanged = computed(() => tempPinCode.value !== (event.value?.PublicPin ?? ''));
    const pinIsValid = computed(() => tempPinCode.value.length === PIN_LENGTH);

    const savePinCode = () => saveEventProperty('PublicPin', tempPinCode.value, $Resources.EventPublicGuestListPinCodeLabel);

    // The guest list's "public guest list" link lands here with #anchor-public-guestlist.
    const route = useRoute();
    onMounted(async () => {
        if (!route.hash) return;
        await nextTick();
        document.querySelector(route.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    const attendanceUrl = computed(() => window.location.origin + '/Events/' + props.eventId + '/Attendance');

    const pollLinks = computed(() => [
        {
            heading: $Resources.EventMentometerAdminHeading,
            infoText: $Resources.EventMentometerAdminInfoText,
            linkText: $Resources.EventMentometerAdminToPageLink,
            url: combineUrlSegments(publicHost.value, 'Events', props.eventId, 'Moderate'),
            requiresPin: true,
        },
        {
            heading: $Resources.EventMentometerLiveHeading,
            infoText: $Resources.EventMentometerLiveInfoText,
            linkText: $Resources.EventMentometerLiveToPageLink,
            url: combineUrlSegments(publicHost.value, 'Events', props.eventId, 'Live'),
            requiresPin: false,
        },
        {
            heading: $Resources.EventMentometerPollHeading,
            infoText: $Resources.EventMentometerPollInfoText,
            linkText: $Resources.EventMentometerPollToPageLink,
            url: combineUrlSegments(publicHost.value, 'Events', props.eventId, 'Poll'),
            requiresPin: false,
        },
    ]);
</script>

<template>
    <div v-if="event" class="max-700">
        <h2 class="h3 m-0-t m-xs-b">{{ $Resources.EventLiveHeading }}</h2>
        <p class="m-xl-b">{{ $Resources.EventLiveText }}</p>

        <div class="event-card">
            <h3 class="event-card-heading">{{ $Resources.EventPinCodeHeading }}</h3>

            <div v-if="canReadContacts" class="form-group">
                <FormInput
                    id="eventLivePublicPinCode"
                    v-model="tempPinCode"
                    :disabled="!canWrite"
                    type="password"
                    autocomplete="off"
                    :maxlength="PIN_LENGTH"
                    :label="$Resources.EventPublicGuestListPinCodeLabel"
                    :placeholder="$Resources.EventPublicGuestListPinCodePlaceholder"
                    style="width: 340px">
                    <template #button>
                        <button type="button" class="btn btn-default" :disabled="!canWrite || !pinChanged || !pinIsValid" @click="savePinCode">
                            {{ $Resources.EventPublicGuestListChangePinCodeBtnText }}
                        </button>
                    </template>
                </FormInput>

                <ul v-if="pinChanged" class="input-requirements list-unstyled">
                    <li :class="{ ok: pinIsValid }">{{ $Resources.EventPublicGuestListPinCodeValidationText }}</li>
                </ul>
            </div>

            <BsAlert v-else :type="AlertTypes.Info" :dismissible="false" :title="$Resources.FeatureLockedHeading" />
        </div>

        <div id="anchor-public-guestlist" class="event-card">
            <h3 class="event-card-heading">
                {{ $Resources.EventPublicGuestListHeading }}
                <i class="fa fa-lock text-md c-light m-xs-l" aria-hidden="true" v-tooltip="$Resources.EventPinCodeNeededLabel"></i>
            </h3>
            <p>{{ $Resources.EventPublicGuestListPinCodeHeading }}</p>

            <p>
                <span class="label-look d-block">{{ $Resources.EventPublicGuestListUrlLabel }}:</span>
                <a :href="attendanceUrl" target="_blank" rel="noopener">{{ attendanceUrl }}</a>
                <button type="button" class="btn btn-subtle btn-xs m-xs-l" @click="copyToClipboard(attendanceUrl)">
                    {{ $Resources.EventPublicGuestCopyUrlBtnText }}
                </button>
            </p>

            <div class="form-group">
                <a :href="attendanceUrl" target="_blank" rel="noopener" class="btn btn-default btn-flex">
                    {{ $Resources.EventLinkToPublicGuestList }}
                    <EventIcon icon="openInNew" :size="16" />
                </a>
            </div>
        </div>

        <div class="event-card">
            <h3 class="event-card-heading">{{ $Resources.EventMentometerHeading }}</h3>
            <p>{{ $Resources.EventMentometerIntroText }}</p>

            <div v-for="link in pollLinks" :key="link.url">
                <h4 class="m-xs-b">
                    {{ link.heading }}
                    <i v-if="link.requiresPin" class="fa fa-lock text-md c-light m-xs-l" aria-hidden="true" v-tooltip="$Resources.EventPinCodeNeededLabel"></i>
                </h4>

                <p class="m-xs-b">{{ link.infoText }}</p>

                <p>
                    <a :href="link.url" target="_blank" rel="noopener">{{ link.url }}</a>
                    <button type="button" class="btn btn-subtle btn-xs m-xs-l" @click="copyToClipboard(link.url)">
                        {{ $Resources.EventMentometerCopyBtn }}
                    </button>
                </p>

                <div class="form-group">
                    <a :href="link.url" target="_blank" rel="noopener" class="btn btn-default btn-flex btn-sm">
                        {{ link.linkText }}
                        <EventIcon icon="openInNew" :size="16" />
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>
